/**
 * The Games page's store (`games-store.ts`), bound to the page: the live read
 * and write for this brand (the brand read, any member and staff by their
 * role; the catalog once per session per viewer kind, shared with the places
 * pages; `updateBrand`), or a sample brand under `?fixtures=1` (nothing sent
 * anywhere). Read when the page opens; its timers cleared when it goes.
 * Mount it once per brand (`key`): a brand left behind keeps nothing here.
 */
import * as React from "react";

import { fetchBrand, updateBrand } from "@/data/brands-api";
import { readGameCatalogOnce } from "@/data/place-games-api";
import { usePortal } from "@/features/portal/portal-context";
import { useFixtureMode } from "@/features/places/use-places";
import { isLostAnswer } from "@/features/writes/lost-answer";
import type { BrandGamesApi } from "@/features/games/brand-games";
import { fixtureBrandGamesApi, type SampleBrandKey } from "@/features/games/fixtures";
import { createGamesStore, type GamesState, type GamesStore } from "@/features/games/games-store";

/** The live read and write for one brand. */
function liveApi(brandId: string, isSuperAdmin: boolean): BrandGamesApi {
  return {
    read: async () => {
      const [brand, catalog] = await Promise.all([fetchBrand(brandId), readGameCatalogOnce({ isSuperAdmin })]);
      return brand ? { slugs: brand.allowedGameSlugs, catalog } : null;
    },
    write: async (slugs) => {
      await updateBrand({ id: brandId, allowedGameSlugs: slugs });
    },
  };
}

export function useBrandGames(brandId: string, sampleBrand: SampleBrandKey): [GamesState, GamesStore] {
  const { isSuperAdmin } = usePortal();
  const sampleMode = useFixtureMode();
  const store = React.useMemo(
    () =>
      createGamesStore({
        api: sampleMode ? fixtureBrandGamesApi(sampleBrand, isSuperAdmin) : liveApi(brandId, isSuperAdmin),
        sample: sampleMode,
        isLost: isLostAnswer,
      }),
    [sampleMode, sampleBrand, brandId, isSuperAdmin],
  );
  React.useEffect(() => {
    store.start();
    return () => store.dispose();
  }, [store]);
  const state = React.useSyncExternalStore(store.subscribe, store.getState, store.getState);
  return [state, store];
}
